import { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Bot, Send, User, Loader2 } from "lucide-react";
import axios from "axios";

const API_URL = typeof window !== 'undefined' && window.location.hostname !== 'localhost'
  ? `${window.location.origin}/api`
  : `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const AiAssistant = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([
    { role: "assistant", content: `Hej ${user?.name?.split(" ")[0]}! Jag är Mälair AI. Fråga mig om projekt, timmar, ekonomi eller personal.` }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const question = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: question }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/ai/chat`, { message: question, session_id: "main" }, { headers: getAuthHeader() });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (e) {
      toast.error("AI svarade inte");
      setMessages(prev => [...prev, { role: "assistant", content: "Kunde inte svara just nu. Försök igen." }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const suggestions = [
    "Hur många timmar har jobbats denna vecka?",
    "Vilka har inte rapporterat idag?",
    "Hur ser vinsten ut denna månad?",
    "Vilka projekt är aktiva?",
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-6rem)]" data-testid="ai-assistant">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#6dbf4a]/15 flex items-center justify-center">
          <Bot className="w-5 h-5 text-[#6dbf4a]" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight">Mälair AI</h1>
          <p className="text-xs text-white/25">Projektassistent med realtidsdata</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 pb-4 pr-1">
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-3 ${msg.role === "user" ? "justify-end" : ""}`}>
            {msg.role === "assistant" && (
              <div className="w-7 h-7 rounded-lg bg-[#6dbf4a]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Bot className="w-3.5 h-3.5 text-[#6dbf4a]" />
              </div>
            )}
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
              msg.role === "user" 
                ? "bg-[#6dbf4a]/15 text-white ml-auto"
                : "bg-white/[0.04] text-white/80 border border-white/[0.06]"
            }`}>
              {msg.content}
            </div>
            {msg.role === "user" && (
              <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <User className="w-3.5 h-3.5 text-white/50" />
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex gap-3">
            <div className="w-7 h-7 rounded-lg bg-[#6dbf4a]/15 flex items-center justify-center flex-shrink-0">
              <Loader2 className="w-3.5 h-3.5 text-[#6dbf4a] animate-spin" />
            </div>
            <div className="bg-white/[0.04] border border-white/[0.06] rounded-2xl px-4 py-2.5 text-sm text-white/40">
              Tänker...
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Suggestions (only show if few messages) */}
      {messages.length <= 2 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {suggestions.map((s, i) => (
            <button key={i} onClick={() => { setInput(s); }}
              className="text-xs px-3 py-1.5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-white/40 hover:text-white/70 hover:bg-white/[0.06] transition-all">
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="flex gap-2">
        <Input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Ställ en fråga..."
          className="flex-1 h-11 rounded-xl bg-white/[0.04] border-white/[0.06] text-white placeholder:text-white/20"
          data-testid="ai-input"
          disabled={loading}
        />
        <Button onClick={handleSend} disabled={loading || !input.trim()}
          className="h-11 w-11 rounded-xl bg-[#6dbf4a] hover:bg-[#5da83d] text-white p-0" data-testid="ai-send">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default AiAssistant;
